import { useState } from "react";
import { AlertTriangle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import {
  extractImportErrorMessage,
  useCommitImport,
  useImportToCreateSummary,
} from "@/api/import";
import { translateImportDisclaimer } from "@/lib/import/import-server-i18n";
import {
  ImportAnalysisReport,
  ImportWriteMode,
} from "@/lib/import/import-types";

/**
 * Recapitulatif, puis **le bouton qui ecrit**.
 *
 * Trois gardes avant l'ecriture, dans cet ordre :
 *
 *   1. le **mode d'ecriture** est formule par consequence, pas par jargon ;
 *   2. les **disclaimers** sont des cases obligatoires, et leur validation est
 *      horodatee sur le job cote serveur ;
 *   3. le bouton est desactive pendant l'envoi, et le serveur refuse un second
 *      lancement par compare-and-swap sur le statut — trois couches contre le
 *      double clic.
 */
export const ImportStepConfirm = ({
  jobId,
  report,
  onStarted,
  onBack,
}: {
  jobId: string;
  report: ImportAnalysisReport;
  onStarted: () => void;
  onBack: () => void;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [writeMode, setWriteMode] = useState<ImportWriteMode>("createOnly");
  const [verified, setVerified] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [acceptUpdate, setAcceptUpdate] = useState(false);

  const { mutateAsync: commit, isPending } = useCommitImport();
  // Récap des créations de fiches liées (auteurs/collections absents…), détaillé
  // par type, pour ne pas découvrir après coup ce que l'import va créer.
  const { data: createSummary } = useImportToCreateSummary(jobId);
  const byType = Object.entries(createSummary?.byType ?? {}).filter(
    ([, names]) => names.length > 0,
  );
  const linkedToCreate = byType.reduce((sum, [, names]) => sum + names.length, 0);
  const typeLabel = (type: string) =>
    ({
      author: vt("bo.import.report.createdAuthors", "auteur(s)"),
      collector: vt("bo.import.report.createdCollectors", "collectionneur(s)"),
      ensemble: vt("bo.import.report.createdEnsembles", "ensemble(s)"),
    })[type] ?? type;

  const needsUpdateConsent = writeMode === "createAndUpdate";
  const allChecked =
    verified && understood && (!needsUpdateConsent || acceptUpdate);

  const modes: Array<{
    value: ImportWriteMode;
    title: string;
    description: string;
    warning?: string;
  }> = [
    {
      value: "createOnly",
      title: vt("bo.import.confirm.createOnly", "Créer uniquement"),
      description: vt(
        "bo.import.confirm.createOnlyHint",
        "Les lignes dont le numéro d'enregistrement existe déjà seront ignorées et listées dans le rapport.",
      ),
    },
    {
      value: "createAndUpdate",
      title: vt("bo.import.confirm.createAndUpdate", "Créer et mettre à jour"),
      description: vt(
        "bo.import.confirm.createAndUpdateHint",
        "Les fiches existantes seront modifiées avec les valeurs de votre fichier. Les colonnes absentes de votre fichier ne seront pas touchées.",
      ),
      warning: vt(
        "bo.import.confirm.createAndUpdateWarning",
        "Cette action ne peut pas être annulée automatiquement.",
      ),
    },
  ];

  const launch = async () => {
    try {
      await commit({
        jobId,
        writeMode,
        emptyMeansClear: false,
        confirmDisclaimers: true,
      });
      onStarted();
    } catch (error) {
      const { message } = extractImportErrorMessage(error);
      toast({
        variant: "destructive",
        title:
          message ??
          vt("bo.import.confirm.startError", "L'import n'a pas pu être lancé."),
      });
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <section className="flex flex-col gap-2">
        <h3 className="text-base font-semibold">
          {vt("bo.import.confirm.summary", "Ce qui va être écrit")}
        </h3>
        <p className="text-sm text-gray-700">
          {report.rowCount.toLocaleString("fr-FR")}{" "}
          {report.rowCount > 1
            ? vt("bo.import.confirm.rowsToWrite", "lignes seront traitées")
            : vt("bo.import.confirm.rowToWrite", "ligne sera traitée")}
          {" · "}
          {report.mappedColumnCount}{" "}
          {vt("bo.import.confirm.columnsUsed", "colonnes utilisées")}
        </p>
        {linkedToCreate > 0 && (
          <div className="flex gap-2 text-sm text-blue-800 p-3 rounded-xl border border-blue-200 bg-blue-50">
            <Plus className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <div className="flex flex-col gap-1">
              <span>
                {vt(
                  "bo.import.confirm.linkedToCreate",
                  "Nouvelles fiches liées qui seront créées automatiquement :",
                )}
              </span>
              <ul className="flex flex-col gap-1.5">
                {byType.map(([type, names]) => (
                  <li key={type}>
                    <span className="font-medium">
                      {names.length.toLocaleString("fr-FR")} {typeLabel(type)}
                    </span>
                    {" : "}
                    {names.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <Separator />

      <section className="flex flex-col gap-3">
        <h3 className="text-base font-semibold">
          {vt("bo.import.confirm.mode", "Que faire des fiches déjà existantes ?")}
        </h3>
        <RadioGroup
          value={writeMode}
          onValueChange={(value) => setWriteMode(value as ImportWriteMode)}
          className="flex flex-col gap-3"
        >
          {modes.map((mode) => (
            <label
              key={mode.value}
              className={cn(
                "flex gap-3 p-4 rounded-xl border cursor-pointer",
                writeMode === mode.value
                  ? "border-brand-300 ring-4 ring-brand-300/25"
                  : "border-gray-200 hover:border-gray-300",
              )}
            >
              <RadioGroupItem value={mode.value} className="mt-1" />
              <div className="flex flex-col gap-1">
                <span className="font-medium">{mode.title}</span>
                <span className="text-sm text-gray-600">{mode.description}</span>
                {mode.warning && writeMode === mode.value && (
                  <span className="flex items-center gap-1.5 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {mode.warning}
                  </span>
                )}
              </div>
            </label>
          ))}
        </RadioGroup>
      </section>

      <Separator />

      <section className="flex flex-col gap-3">
        <h3 className="text-base font-semibold">
          {vt("bo.import.confirm.disclaimers", "Avant de lancer")}
        </h3>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={verified}
            onCheckedChange={(value) => setVerified(value === true)}
            className="mt-0.5"
          />
          <span>
            {vt(
              "bo.import.confirm.checkVerified",
              "J'ai vérifié la correspondance de mes colonnes et les liaisons proposées.",
            )}
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={understood}
            onCheckedChange={(value) => setUnderstood(value === true)}
            className="mt-0.5"
          />
          <span>
            {vt(
              "bo.import.confirm.checkIrreversible",
              "Je comprends qu'un import ne s'annule pas d'un clic : les fiches créées devront être supprimées une par une.",
            )}
          </span>
        </label>

        {needsUpdateConsent && (
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={acceptUpdate}
              onCheckedChange={(value) => setAcceptUpdate(value === true)}
              className="mt-0.5"
            />
            <span>
              {vt(
                "bo.import.confirm.checkWillUpdate",
                "J'accepte que les fiches existantes soient modifiées.",
              )}
            </span>
          </label>
        )}

        {report.disclaimers.length > 0 && (
          <ul className="flex flex-col gap-1 text-xs text-gray-600 list-disc pl-4 pt-1">
            {report.disclaimers.map((disclaimer) => (
              <li key={disclaimer}>
                {translateImportDisclaimer(t, disclaimer)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
        <Button variant="ghost" onClick={onBack} disabled={isPending}>
          {vt("bo.import.back", "Retour")}
        </Button>
        <LoaderButton
          isLoading={isPending}
          disabled={!allChecked || isPending}
          onClick={launch}
          title={
            !allChecked
              ? vt(
                  "bo.import.confirm.checkAllFirst",
                  "Cochez les cases ci-dessus pour lancer l'import.",
                )
              : undefined
          }
        >
          {vt("bo.import.confirm.launch", "Lancer l'import")}
        </LoaderButton>
      </div>
    </div>
  );
};
